import { Timer as TimerIcon } from "lucide-react";
import { useNow } from "@/shared/dashboard/timer";
import { cn } from "@/shared/lib/utils";
import { formatElapsed } from "./format";

type TimerDisplayProps = {
  /** Início (Date/epoch ms). null → "—". */
  since: Date | number | null | undefined;
  /** "up" = tempo decorrido; "down" = regressivo até `limitMs` (excedente aparece negativo, em perigo). */
  mode?: "up" | "down";
  limitMs?: number;
  /** Decorrido a partir do qual o timer fica em alerta (âmbar). */
  warnAfterMs?: number;
  /** Decorrido a partir do qual o timer fica em perigo (vermelho). */
  dangerAfterMs?: number;
  alwaysHours?: boolean;
  size?: "sm" | "md" | "lg";
  icon?: boolean;
  className?: string;
  /** Rótulo acessível (ex.: "Tempo no status"). */
  label?: string;
};

/**
 * Cronômetro ao vivo com dígitos tabulares (não "pula" de largura), usando o tick
 * compartilhado de 1s do dashboard (um único setInterval para a tela toda).
 */
export function TimerDisplay({
  since,
  mode = "up",
  limitMs = 0,
  warnAfterMs,
  dangerAfterMs,
  alwaysHours,
  size = "sm",
  icon,
  className,
  label,
}: TimerDisplayProps) {
  const begin = since == null ? null : typeof since === "number" ? since : since.getTime();
  const now = useNow(begin != null);
  if (begin == null || Number.isNaN(begin)) return <span className={cn("text-fg-subtle", className)}>—</span>;

  const elapsed = Math.max(0, now - begin);
  let text: string;
  let state: "normal" | "warn" | "danger" = "normal";
  if (mode === "down") {
    const remaining = limitMs - elapsed;
    text = remaining < 0 ? `-${formatElapsed(-remaining, alwaysHours)}` : formatElapsed(remaining, alwaysHours);
    if (remaining < 0) state = "danger";
    else if (warnAfterMs != null && elapsed >= warnAfterMs) state = "warn";
  } else {
    text = formatElapsed(elapsed, alwaysHours);
    if (dangerAfterMs != null && elapsed >= dangerAfterMs) state = "danger";
    else if (warnAfterMs != null && elapsed >= warnAfterMs) state = "warn";
  }

  return (
    <span
      role="timer"
      aria-label={label}
      className={cn(
        "tnum inline-flex items-center gap-1 font-mono font-medium",
        size === "sm" ? "text-caption" : size === "md" ? "text-body" : "text-title",
        state === "danger" ? "text-danger-fg" : state === "warn" ? "text-warning-fg" : "text-fg",
        className,
      )}
    >
      {icon ? <TimerIcon className={size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5"} aria-hidden /> : null}
      {text}
    </span>
  );
}
